import { useCallback } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Control, FieldErrors, FieldValues, UseFormRegister, useFieldArray } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { ZodObject, ZodRawShape } from 'zod';
import { ApiError } from '../../../api';
import { ShowFieldErrors } from '../../../utils/forms';
import { useSafeParseFormField } from '../../hooks/safe-parse-form-field';
import { FileUploader } from '../FileUploader';
import { FormFeedback } from '../FormFeedback';
import { FormFieldVisibility, FormLabel } from '../FormLabel';
import { FormTextField } from '../FormTextField';
import { FORM_NAME, MAX_SIZE, ACCEPT } from './constants';

interface MiscellaneousFormGroupProps {
  control: Control;
  errors: FieldErrors;
  serverErrors: ApiError | undefined;
  register: UseFormRegister<FieldValues>;
  showServerFieldErrors: ShowFieldErrors | undefined;
  setShowServerFieldErrors: (showServerFieldErrors: ShowFieldErrors) => void;
  validationSchema: ZodObject<ZodRawShape>;
}

export const MiscellaneousFormGroup = ({
  control,
  errors,
  serverErrors,
  showServerFieldErrors,
  setShowServerFieldErrors,
  register,
  validationSchema,
}: MiscellaneousFormGroupProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_NAME,
  });
  const isParsed = useSafeParseFormField(control, FORM_NAME, validationSchema);
  const onAppendClick = useCallback(() => {
    append({
      file__file: null,
      title: null,
      exempt: false,
      issue_date: null,
      expiration_date: null,
    });
  }, [append]);

  return (
    <Form.Group className="mb-3">
      <FormLabel visibility={FormFieldVisibility.CONNECTED}>Other documents</FormLabel>
      <div className="text-body-tertiary mb-2">
        Add any other information useful for Healthcare Organizations
      </div>
      <ul className="list-unstyled">
        {fields.map((item, index) => (
          <li key={item.id} className="mt-4 border rounded p-3">
            <Row>
              <Col xs={12}>
                <Row className="justify-content-end align-items-center mb-4">
                  <Col>
                    <span className="text-primary fw-bold">Record #{index + 1}</span>
                  </Col>
                  <Col xs="auto">
                    <Button
                      type="button"
                      variant="link"
                      className="text-danger"
                      size="lg"
                      onClick={() => remove(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} className="mb-4">
                    <Form.Group controlId={`${FORM_NAME}.${index}.file__file`}>
                      <FileUploader
                        accept={ACCEPT}
                        maxSize={MAX_SIZE}
                        name={`${FORM_NAME}.${index}.file__file`}
                        disabledPreview={true}
                        control={control}
                        className="mb-3"
                        dest="providers_documents"
                        isClearable={false}
                        isInvalid={
                          !!errors[FORM_NAME]?.[index]?.['file__file'] ||
                          !!showServerFieldErrors?.[index]?.['file__file']
                        }
                      />
                      <FormFeedback
                        clientError={errors[FORM_NAME]?.[index]?.['file__file']}
                        serverErrors={serverErrors?.body[FORM_NAME]?.[index]?.file}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} className="mb-3">
                    <FormTextField
                      type="text"
                      name={`${FORM_NAME}.${index}.title`}
                      label="Title"
                      required
                      serverErrors={serverErrors}
                      showServerFieldErrors={showServerFieldErrors}
                      setShowServerFieldErrors={setShowServerFieldErrors}
                      errors={errors}
                      register={register}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <FormTextField
                      type="date"
                      name={`${FORM_NAME}.${index}.issue_date`}
                      label="Date Given"
                      serverErrors={serverErrors}
                      showServerFieldErrors={showServerFieldErrors}
                      setShowServerFieldErrors={setShowServerFieldErrors}
                      errors={errors}
                      register={register}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <FormTextField
                      type="date"
                      name={`${FORM_NAME}.${index}.expiration_date`}
                      label="Expiration Date"
                      serverErrors={serverErrors}
                      showServerFieldErrors={showServerFieldErrors}
                      setShowServerFieldErrors={setShowServerFieldErrors}
                      errors={errors}
                      register={register}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </li>
        ))}
      </ul>
      <Button
        className="d-flex align-items-center"
        variant="outline-primary"
        type="button"
        disabled={!!fields.length && !isParsed}
        onClick={onAppendClick}
      >
        <BsPlus className="me-1" /> Add Record
      </Button>
    </Form.Group>
  );
};
